import { useRef } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { getPublicationToken } from '../api/client'

// Rebuild URL 5 minutes before the 1-hour JWT expiry
const TOKEN_REFRESH_BUFFER_MS = 55 * 60 * 1000

/**
 * Hook to build authenticated media URLs for video/audio playback.
 *
 * The issue: <video> and <audio> tags can't send Authorization headers,
 * so we append the JWT as a query parameter for the backend to authenticate.
 *
 * The URL is stable once built — token refreshes do NOT immediately change
 * the URL, preventing <video>/<audio> elements from reloading mid-playback.
 * The URL is only rebuilt when:
 *  - The baseUrl or cacheKey changes (new content)
 *  - The cached token is older than 55 minutes (approaching expiry)
 *
 * Falls back to the publication token for unauthenticated viewers of
 * published projects.
 *
 * @param baseUrl - The base URL without authentication (or null)
 * @returns The URL with ?token=<jwt> or ?publication_token=<token> appended (or null if baseUrl is null)
 */
export function useAuthenticatedMediaUrl(baseUrl: string | null, cacheKey?: string): string | null {
  const { session } = useAuth()
  const token = session?.access_token ?? null
  const cachedRef = useRef<{
    baseUrl: string | null
    cacheKey?: string
    token: string | null
    url: string | null
    builtAt: number
  }>({
    baseUrl: null,
    cacheKey: undefined,
    token: null,
    url: null,
    builtAt: 0,
  })

  if (!baseUrl) return null
  // Signed Supabase URLs (from published snapshots) are already authenticated
  if (baseUrl.startsWith('https://') && baseUrl.includes('/storage/v1/object/sign/')) {
    return baseUrl
  }

  const cached = cachedRef.current
  const tokenExpired = Date.now() - cached.builtAt > TOKEN_REFRESH_BUFFER_MS
  const contentChanged = cached.baseUrl !== baseUrl || cached.cacheKey !== cacheKey

  if (!contentChanged && !tokenExpired) {
    return cached.url
  }

  // Rebuild URL with the latest token
  const params = new URLSearchParams()
  if (token) {
    params.set('token', token)
  }
  const pubToken = getPublicationToken()
  if (pubToken) {
    params.set('publication_token', pubToken)
  }
  if (cacheKey) params.set('_', cacheKey)
  const qs = params.toString()
  const url = qs ? `${baseUrl}?${qs}` : baseUrl

  cachedRef.current = { baseUrl, cacheKey, token, url, builtAt: Date.now() }
  return url
}
